<!-- File path: src/components/common/SignupForm.svelte -->
<script>
  import { update_subscription_status, is_subscribed_store } from '../../lib/stores.js';
  import { getFunctions, httpsCallable, connectFunctionsEmulator } from 'firebase/functions';
  import { app } from '../../lib/firebase.js';
  let form_element;
  let is_submitting = false;
  let error_message = '';
  let email_consent = false;
  let show_consent_warning = false;
  let email = '';
  let fullname = '';
  let first_name = '';
  let last_name = '';
  let company = '';
  let phone = '';
  let list_id = '';
  
  // --- Validation --- 
  /**
   * @function validate_email
   * @description Basic email format validation.
   * @param {string} email_string - The email to validate.
   * @returns {boolean} True if the email format looks valid.
   */
  function validate_email(email_string) {
    // Basic check for presence of @ and .
    // This is not foolproof, but good for client-side UX.
    const email_regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return email_regex.test(email_string);
  }

  // Reactive variable to determine if the form is valid for submission
  $: is_form_valid = 
    validate_email(email) && 
    fullname.trim() !== '' && 
    email_consent;

  /**
   * @function handle_submit
   * @description Handles the form submission process: validation, calling Firebase function, handling response.
   */
  async function handle_submit() {
    // If the form isn't valid (e.g., submitted programmatically or bypassing disabled state), show error.
    if (!is_form_valid) {
      error_message = 'Please ensure all fields are correctly filled and consent is given.';
      return;
    }
    
    // Reset error message before attempting submission
    error_message = '';

    try {
      console.log('Form submission started');
      is_submitting = true;

      // Get the Firebase Functions instance
      const functions = getFunctions();
      
      // Connect to emulator in development using the default port (5001).
      // Note: If the functions emulator port is changed in firebase.json,
      // this port number will need to be updated accordingly.
      if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
        console.log('Connecting to emulator on localhost:5001');
        connectFunctionsEmulator(functions, 'localhost', 5001);
      }
      
      const subscribe = httpsCallable(functions, 'subscribe');
      console.log('Calling subscribe function with:', { email, fullname });
      const result = await subscribe({
        email: email,
        fullname: fullname
      });
      console.log('Subscribe function result:', result);

      // Safely check for success in the result data
      if (result && typeof result.data === 'object' && result.data !== null && result.data['success'] === true) {
        console.log('Subscription successful, updating store');
        email = '';
        fullname = '';
        first_name = '';
        last_name = '';
        company = '';
        phone = '';
        // Update both the store and cookie when subscription is successful
        update_subscription_status(true);
      } else {
        console.log('Subscription failed:', result && result.data ? result.data : 'No result data or failed status');
        throw new Error('Failed to subscribe');
      }
    } catch (error) {
      console.error('Subscription error:', error);
      error_message = error.message || 'Failed to subscribe. Please try again.';
    } finally {
      is_submitting = false;
    }
  }
</script>

{#if $is_subscribed_store}
  <div 
    class="success_message already_subscribed" 
    data-test-id="signup-success-message"
  >
    <h3>You are subscribed!</h3>
    <p>Thanks for being part of Down to Earth Guides. Check your inbox for updates!</p>
  </div>
{:else} 
  <div class="signup_form_wrapper">
    <form 
      bind:this={form_element}
      on:submit|preventDefault={handle_submit}
      class="signup_form"
      data-test-id="signup-form"
    >
      <div class="form_header">
        <h3>Join Down to Earth Guides</h3>
        <p>We'll notify you when we create new lessons. We never share our list.</p>
      </div>

      <div class="form_field">
        <label for="fullname">Full Name</label>
        <input 
          type="text" 
          id="fullname" 
          name="fullname" 
          bind:value={fullname}
          placeholder="Type your name"
          data-test-id="signup-fullname-input"
        />
      </div>

      <div class="form_field">
        <label for="email">
          Email <span class="required">*</span>
        </label>
        <input 
          type="email" 
          id="email" 
          name="email" 
          bind:value={email}
          placeholder="Type your email"
          required
          data-test-id="signup-email-input"
        />
      </div>

      <div class="form_field checkbox_field">
        <label class="checkbox_label">
          <input 
            type="checkbox" 
            bind:checked={email_consent}
            on:change={() => show_consent_warning = !email_consent}
            data-test-id="signup-consent-checkbox"
          />
          <span>It is ok to send me emails at this address.</span>
        </label>
      </div>

      {#if show_consent_warning}
        <div class="consent_warning">
          Please check the box to let us send you emails.
        </div>
      {/if}

      {#if error_message}
        <div class="error_message">
          {error_message}
        </div>
      {/if}

      <button 
        type="submit" 
        class="submit_button"
        class:ready={is_form_valid}
        data-test-id="signup-submit-button"
      >
        {is_submitting ? 'Joining...' : 'Join Now'}
      </button>
    </form>
  </div>
{/if}

<style>
  :global(:root) {
    /* Define success colors - move these to your global CSS variables file */
    --color-success: #4CAF50;
    --color-success-dark: #388E3C;
  }

  .signup_form_wrapper {
    width: 100%;
    max-width: 500px;
    margin: 0 auto;
    padding: 2rem;
  }

  .signup_form {
    background: var(--color-card-bg);
    border-radius: 12px;
    padding: 2rem;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  }

  .form_header {
    margin-bottom: 2rem;
    text-align: left;
  }

  .form_header h3 {
    color: var(--color-primary);
    font-size: 1.5rem;
    margin: 0 0 0.5rem 0;
    font-weight: 600;
  }

  .form_header p {
    color: var(--color-text-muted);
    font-size: 0.9rem;
    margin: 0;
  }

  .form_field {
    margin-bottom: 1.5rem;
  }

  .checkbox_field {
    margin-bottom: 1rem;
  }

  .checkbox_label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    cursor: pointer;
  }

  .checkbox_label input[type="checkbox"] {
    width: auto;
    margin: 0;
  }

  .checkbox_label span {
    font-size: 0.9rem;
    color: var(--color-text);
  }

  label {
    display: block;
    color: var(--color-text);
    font-weight: 500;
    margin-bottom: 0.5rem;
  }

  .required {
    color: var(--color-secondary);
    margin-left: 0.25rem;
  }

  input {
    width: 100%;
    padding: 0.75rem 1rem;
    border: 1px solid var(--color-card-hover);
    border-radius: 6px;
    background: var(--color-background);
    color: var(--color-text);
    font-size: 1rem;
    transition: all 0.2s ease;
  }

  input:focus {
    outline: none;
    border-color: var(--color-primary);
    box-shadow: 0 0 0 2px rgba(127, 219, 202, 0.2);
  }

  input::placeholder {
    color: var(--color-text-muted);
    opacity: 0.7;
  }

  .submit_button {
    width: 100%;
    padding: 1rem;
    border: none;
    border-radius: 6px;
    background: var(--color-primary);
    color: var(--color-background);
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .submit_button:hover:not(:disabled) {
    background: var(--color-secondary);
    transform: translateY(-1px);
  }

  .submit_button:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  .submit_button.ready {
    background: var(--color-success); /* Assuming you have a success color variable */
    color: white; /* Or appropriate contrast color */
  }

  .submit_button.ready:hover {
    background: var(--color-success-dark); /* Darker shade for hover */
  }

  .error_message, .consent_warning {
    padding: 0.75rem;
    margin: 0.5rem 0;
    border-radius: 6px;
    background: rgba(232, 141, 114, 0.1);
    color: var(--color-secondary);
    text-align: center;
  }

  .success_message {
    text-align: center;
    padding: 2rem;
    background: var(--color-card-bg);
    border-radius: 12px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  }

  .success_message h3 {
    color: var(--color-primary);
    font-size: 1.5rem;
    margin: 0 0 1rem 0;
  }

  .success_message p {
    color: var(--color-text);
    margin: 0;
  }

  @media (max-width: 768px) {
    .signup_form_wrapper {
      padding: 1rem;
    }

    .signup_form {
      padding: 1.5rem;
    }

    .form_header h3 {
      font-size: 1.25rem;
    }
  }
</style> 